"use client";

import React, { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  Inbox,
  Loader2,
  Mail,
  Paperclip,
  RefreshCw,
  Reply,
  Search,
  Ticket as TicketIcon,
  X,
} from "lucide-react";
import { MODAL_IDS, useGlobalModalStack } from "@wtc/ui";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SafeEmailBody } from "@/components/crm/messages/components/SafeEmailBody";
import { useCompanyContacts } from "@/features/companies/hooks/companies-base-queries";

import {
  usePrefetchSupportInboxThread,
  useSupportInbox,
  useSupportInboxThread,
  useThreadSummaries,
} from "./useSupportInbox";
import {
  resolveExternalCounterparty,
  supportInboxMessageToMessage,
} from "./supportInboxMessageToMessage";
import { SupportStatusBadge } from "./SupportStatusBadge";
import { hasVisibleAttachments } from "./attachment-visibility";
import { SupportInboxAttachments } from "./SupportInboxAttachments";
import type {
  SupportInboxListMessage,
  SupportInboxThreadMessage,
  SupportInboxThreadSummary,
} from "./types";

interface CompanySupportInboxDisplayProps {
  companyId: string;
  companyName?: string | null;
}

/**
 * A message shown in the reading pane. Bodies are optional so the pane can
 * render instantly from the list-level data already in hand (which carries no
 * body — only a snippet) and upgrade to full HTML/text once the per-thread fetch
 * resolves.
 */
type ReadingMessage = SupportInboxListMessage & {
  htmlContent?: string;
  textContent?: string | null;
};

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Format the sender header ("Jane <jane@x>") for display, preferring the name. */
function displayFrom(from: string): string {
  const angle = from.match(/^(.*)<[^>]+>\s*$/);
  if (angle && angle[1].trim()) {
    return angle[1].trim().replace(/^"(.*)"$/, "$1").trim();
  }
  return from.trim();
}

/** Two-letter avatar initials from a name or email, always uppercase. */
function initialsOf(label: string): string {
  const trimmed = (label ?? "").trim();
  if (!trimmed) return "?";
  const namey = trimmed.replace(/<[^>]*>/g, "").trim() || trimmed;
  const parts = namey.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return namey.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic muted background for an avatar, keyed on the label. */
function avatarTone(label: string): string {
  const tones = [
    "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
    "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
    "bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300",
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) hash = (hash * 31 + label.charCodeAt(i)) | 0;
  return tones[Math.abs(hash) % tones.length];
}

/** Whether any message in a thread carries a listable attachment. */
function threadHasAttachments(thread: SupportInboxThreadSummary): boolean {
  return thread.messages.some((message) => hasVisibleAttachments(message.attachments));
}

/** Plain-text ticket description built from a support email. */
function buildTicketDescription(msg: SupportInboxListMessage): string {
  return [
    `From: ${msg.from}`,
    `Received: ${formatWhen(msg.receivedAt)}`,
    "",
    msg.snippet || "(No preview available — see the support inbox for the full email.)",
  ].join("\n");
}

export function CompanySupportInboxDisplay({
  companyId,
  companyName,
}: CompanySupportInboxDisplayProps) {
  const { openModal } = useGlobalModalStack();
  const inboxQuery = useSupportInbox(companyId);
  const threads = useThreadSummaries(inboxQuery.data);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const threadQuery = useSupportInboxThread(companyId, selectedThreadId);
  const prefetchThread = usePrefetchSupportInboxThread(companyId);

  // The company's primary contact seeds create-ticket attribution.
  const { data: companyContacts } = useCompanyContacts(companyId, { limit: 1 });
  const primaryContactId = useMemo<string | undefined>(() => {
    const first = companyContacts?.[0];
    return first && typeof first.id === "string" ? first.id : undefined;
  }, [companyContacts]);

  const enrolled = inboxQuery.data?.enrolled ?? false;
  const groupEmail = inboxQuery.data?.groupEmail ?? null;

  // The real external correspondent per thread (customer to reply to), resolved
  // from Reply-To / From / To — NOT the "via <group>" From header. Drives the
  // list rows, the reading-pane banner, and the reply-target hint so the operator
  // always sees (and replies to) the actual person, never the group inbox.
  const correspondentByThread = useMemo(() => {
    const map = new Map<string, { name: string; email: string } | null>();
    for (const thread of threads) {
      map.set(thread.threadId, resolveExternalCounterparty(thread.messages, groupEmail));
    }
    return map;
  }, [threads, groupEmail]);

  // Client-side filter over the loaded threads: subject, correspondent, raw from,
  // and each message snippet. Kept in-memory — the list is already bounded by the
  // API's lookback window, so no extra round-trip.
  const filteredThreads = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((thread) => {
      const correspondent = correspondentByThread.get(thread.threadId);
      const haystack = [
        thread.subject,
        thread.from,
        correspondent?.name ?? "",
        correspondent?.email ?? "",
        ...thread.messages.map((m) => m.snippet ?? ""),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [threads, query, correspondentByThread]);

  const handleEnroll = useCallback(() => {
    openModal(MODAL_IDS.companies.enrollSupportGroup, {
      companyId,
      companyName: companyName ?? undefined,
    });
  }, [openModal, companyId, companyName]);

  const handleReply = useCallback(
    (msg: SupportInboxListMessage | SupportInboxThreadMessage) => {
      // Reply to the EXTERNAL customer of the thread, never the support group or a
      // WTC staffer. A support thread interleaves the customer's inbound mail with
      // WTC's @westtn.consulting replies, so seeding the recipient from the clicked
      // message alone can wrongly target the group address.
      const thread = threads.find((t) => t.threadId === msg.threadId);
      const threadMessages = thread?.messages ?? [msg];
      const external = resolveExternalCounterparty(threadMessages, groupEmail);
      const base = supportInboxMessageToMessage(msg);
      // Set the resolved customer as `replyTo` rather than overwriting `sender`:
      // the compose modal resolves the reply recipient from `replyTo[0]` (see
      // EMAIL_REPLY_TARGETS_REPLY_TO), and leaving `sender` intact keeps the
      // quoted "From:" line truthful about who actually wrote the message.
      const message = external ? { ...base, replyTo: [external] } : base;
      openModal(MODAL_IDS.messages.compose, {
        mode: "reply",
        message,
        companyId,
        // The compose send path only reaches the customer directly — it never
        // touches the group inbox, so a reply sent from here is otherwise
        // invisible to Hub and to anyone else looking at this thread. BCC'ing
        // the group's own address redelivers the reply into it, which the
        // existing thread fetch (getGroupThread) will pick up on next refresh.
        initialBcc: groupEmail ? [groupEmail] : undefined,
      });
    },
    [openModal, companyId, threads, groupEmail],
  );

  const handleCreateTicket = useCallback(
    (msg: SupportInboxListMessage) => {
      openModal(MODAL_IDS.tickets.createQuick, {
        companyId,
        contactId: primaryContactId,
        initialSubject: msg.subject,
        initialDescription: buildTicketDescription(msg),
      });
    },
    [openModal, companyId, primaryContactId],
  );

  // Loading (first load only — don't blank a populated view on refetch).
  if (inboxQuery.isLoading && !inboxQuery.data) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading support inbox…
      </div>
    );
  }

  if (inboxQuery.isError) {
    return (
      <div className="space-y-3 p-4">
        <p className="text-sm text-destructive">
          We couldn&apos;t load the support inbox.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void inboxQuery.refetch()}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  // Not enrolled → empty state with the enroll CTA.
  if (!enrolled) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Inbox className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
          <h3 className="text-base md:text-lg font-semibold text-card-foreground">
            Support Inbox
          </h3>
        </div>
        <Card>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-card-foreground">
                {companyName || "This company"}
              </span>{" "}
              isn&apos;t enrolled in a Google support group yet. Enroll one so
              inbound support email flows into the Hub as tickets and
              communications — and shows up here in real time.
            </p>
            <Button type="button" onClick={handleEnroll}>
              <Mail className="w-4 h-4 mr-2" />
              Enroll support group
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const threadListVisibleOnMobile = !selectedThreadId;

  // Reading pane: render immediately from the list data we already have (headers,
  // snippet, ticket/communication badges), then upgrade to full bodies when the
  // per-thread fetch resolves. This makes the first click feel instant instead of
  // blocking on a Gmail round-trip.
  const selectedThread = selectedThreadId
    ? threads.find((thread) => thread.threadId === selectedThreadId)
    : undefined;
  const fullThreadMessages = threadQuery.data?.messages;
  const readingMessages: ReadingMessage[] = (fullThreadMessages ??
    selectedThread?.messages ??
    []) as ReadingMessage[];
  const readingMessagesNewestFirst = [...readingMessages].sort(
    (a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt),
  );
  const bodiesPending = !fullThreadMessages && threadQuery.isFetching;
  const bodiesFailed = threadQuery.isError && !fullThreadMessages;

  // Who a reply in this thread will actually reach (the resolved customer).
  const selectedCorrespondent = selectedThreadId
    ? correspondentByThread.get(selectedThreadId) ?? null
    : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Inbox className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
          <h3 className="text-base md:text-lg font-semibold text-card-foreground truncate">
            Support Inbox
          </h3>
          {threads.length > 0 && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground shrink-0">
              {threads.length}
            </span>
          )}
          {groupEmail && (
            <span className="hidden sm:inline text-xs text-muted-foreground font-mono truncate">
              {groupEmail}
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void inboxQuery.refetch()}
          className="text-muted-foreground shrink-0"
          title="Refresh"
        >
          <RefreshCw
            className={`w-4 h-4 ${inboxQuery.isFetching ? "animate-spin" : ""}`}
          />
        </Button>
      </div>

      {/* Thread search — hidden on mobile once a thread is open to reclaim space. */}
      <div className={threadListVisibleOnMobile ? "block" : "hidden md:block"}>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search support email…"
            aria-label="Search support email"
            className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-8 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover:text-card-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:h-[70vh] md:min-h-[420px]">
        {/* Thread list (left) */}
        <div
          className={`md:w-2/5 md:max-w-md md:overflow-y-auto md:border-r md:border-border md:pr-3 ${
            threadListVisibleOnMobile ? "block" : "hidden md:block"
          }`}
        >
          {threads.length === 0 ? (
            <p className="text-sm text-muted-foreground p-2">
              No support email yet.
            </p>
          ) : filteredThreads.length === 0 ? (
            <p className="text-sm text-muted-foreground p-2">
              No conversations match “{query}”.
            </p>
          ) : (
            <ul className="space-y-1">
              {filteredThreads.map((thread) => {
                const isSelected = thread.threadId === selectedThreadId;
                const correspondent = correspondentByThread.get(thread.threadId);
                const who =
                  correspondent?.name ||
                  correspondent?.email ||
                  displayFrom(thread.from);
                return (
                  <li key={thread.threadId}>
                    <button
                      type="button"
                      onClick={() => setSelectedThreadId(thread.threadId)}
                      onMouseEnter={() => prefetchThread(thread.threadId)}
                      onFocus={() => prefetchThread(thread.threadId)}
                      className={`flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-transparent hover:bg-muted/50"
                      }`}
                    >
                      <span
                        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${avatarTone(
                          who,
                        )}`}
                        aria-hidden
                      >
                        {initialsOf(who)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-card-foreground">
                            {who}
                          </span>
                          <span className="shrink-0 text-[11px] text-muted-foreground">
                            {formatWhen(thread.latestReceivedAt)}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-card-foreground/80">
                          {thread.subject || "(no subject)"}
                        </span>
                        <span className="mt-1 flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1 truncate text-[11px] text-muted-foreground">
                            {threadHasAttachments(thread) && (
                              <Paperclip
                                className="h-3 w-3 shrink-0"
                                aria-label="Has attachments"
                              />
                            )}
                            {thread.messageCount > 1
                              ? `${thread.messageCount} messages`
                              : "1 message"}
                          </span>
                          <SupportStatusBadge status={thread.status} />
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Reading pane (right) */}
        <div
          className={`flex-1 md:overflow-y-auto ${
            threadListVisibleOnMobile ? "hidden md:block" : "block"
          }`}
        >
          {!selectedThreadId ? (
            <div className="hidden md:flex h-full items-center justify-center text-sm text-muted-foreground">
              Select a conversation to read it.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 md:hidden">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedThreadId(null)}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              </div>

              {/* Conversation header — who this thread is with + one-tap reply that
                  is guaranteed to reach the customer, not the group inbox. */}
              <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${avatarTone(
                      selectedCorrespondent?.name ||
                        selectedCorrespondent?.email ||
                        selectedThread?.subject ||
                        "?",
                    )}`}
                    aria-hidden
                  >
                    {initialsOf(
                      selectedCorrespondent?.name ||
                        selectedCorrespondent?.email ||
                        "?",
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-card-foreground">
                      {selectedThread?.subject || "(no subject)"}
                    </p>
                    {selectedCorrespondent ? (
                      <p className="truncate text-xs text-muted-foreground">
                        Replies go to{" "}
                        <span className="font-medium text-card-foreground">
                          {selectedCorrespondent.email}
                        </span>
                      </p>
                    ) : (
                      <p className="truncate text-xs text-muted-foreground">
                        No external customer detected in this thread.
                      </p>
                    )}
                  </div>
                </div>
                {readingMessagesNewestFirst[0] && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleReply(readingMessagesNewestFirst[0])}
                    className="shrink-0"
                    title={
                      selectedCorrespondent
                        ? `Reply to ${selectedCorrespondent.email}`
                        : "Reply"
                    }
                  >
                    <Reply className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                )}
              </div>

              {bodiesPending && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground px-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading full message…
                </div>
              )}

              {bodiesFailed && (
                <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
                  <p className="text-xs text-destructive">
                    Showing a preview — we couldn&apos;t load the full message.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void threadQuery.refetch()}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </Button>
                </div>
              )}

              {readingMessagesNewestFirst.length === 0 && !bodiesPending ? (
                <p className="text-sm text-muted-foreground p-2">
                  No messages in this conversation.
                </p>
              ) : (
                readingMessagesNewestFirst.map((message) => (
                  <Card key={message.messageId}>
                    <CardContent className="pt-4 space-y-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-card-foreground truncate">
                            {message.subject || "(no subject)"}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {message.from}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {formatWhen(message.receivedAt)}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                          <SupportStatusBadge status={message.badges.status} />
                        </div>
                      </div>

                      <SafeEmailBody
                        body={
                          message.htmlContent ||
                          message.textContent ||
                          message.snippet ||
                          ""
                        }
                        variant="full"
                        collapseQuotedReplies
                      />

                      <SupportInboxAttachments
                        companyId={companyId}
                        threadId={message.threadId}
                        messageId={message.messageId}
                        attachments={message.attachments}
                      />

                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleReply(message)}
                          title={
                            selectedCorrespondent
                              ? `Reply to ${selectedCorrespondent.email}`
                              : "Reply"
                          }
                        >
                          <Reply className="w-4 h-4 mr-2" />
                          Reply
                        </Button>
                        {message.badges.ticketId ? (
                          <Button
                            asChild
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                          >
                            <Link href={`/tickets/${message.badges.ticketId}`}>
                              <TicketIcon className="w-4 h-4 mr-2" />
                              View ticket
                            </Link>
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            onClick={() => handleCreateTicket(message)}
                          >
                            <TicketIcon className="w-4 h-4 mr-2" />
                            Create ticket
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
